/* eslint-disable react/display-name */
import React from 'react'
import { Link } from 'gatsby'
import { useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { Box } from '@theme-ui/components'
import { getRegionPrefix } from '../../../../shared-utils/src/components/utils/region.js'

const StyledLink = styled(Link)`
  font-size: 1rem;
  font-weight: normal;
  color: ${props => props.theme.colors.navLinkColor};
  text-decoration: none;

`

// Default path prefixes for "resource/wiki" app routes (use plain <a>, no Gatsby Link).
// Override at build time via env: GATSBY_CROSS_APP_PATH_PREFIXES (JSON array string).
// Example: GATSBY_CROSS_APP_PATH_PREFIXES='["/wiki","/blog","/resources",...]'
const DEFAULT_RESOURCE_OR_WIKI_PATH_PREFIXES = [
  '/wiki',
  '/middle-east/wiki',
  '/notifications',
  '/holidays-list-',
  '/middle-east/holidays-',
  '/resources',
  '/middle-east/resources',
  '/blog',
  '/middle-east/blog',
  '/guides',
  '/middle-east/guides',
  '/downloadable-resource',
  '/middle-east/downloadable-resource',
  '/reports',
  '/help-admin',
  '/help-ess-web',
  '/help-ess-mobile',
]

function getResourceOrWikiPathPrefixes() {
  if (typeof process !== 'undefined' && process.env?.GATSBY_CROSS_APP_PATH_PREFIXES) {
    try {
      const parsed = JSON.parse(process.env.GATSBY_CROSS_APP_PATH_PREFIXES)
      if (Array.isArray(parsed) && parsed.every(p => typeof p === 'string')) {
        return parsed
      }
    } catch (_) {
      // invalid JSON or shape -> use default
    }
  }
  return DEFAULT_RESOURCE_OR_WIKI_PATH_PREFIXES
}

/** Match paths served by resource/wiki apps (not main Gatsby). */
function isCrossAppTarget(path, prefixes) {
  const pathname = path.split('#')[0].split('?')[0]
  return prefixes.some(prefix => {
    if (prefix.endsWith('-')) {
      return pathname.includes(prefix)
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })
}

const RegionalLink = props => {
  const { to, children, region, regional, ...otherProps } = props
	var pageRegionCode = region
	if (!region){
		//****Getting Region from date when it is not passed.*/
		pageRegionCode = useSelector((state) => state.app.region);
	}
  const regionPath =
    pageRegionCode && regional && getRegionPrefix(pageRegionCode)
      ? `/${getRegionPrefix(pageRegionCode)}${to}`
      : to

  // Determine if we're currently inside a resource or wiki app (paths from env or default).
  // When on any of these, use plain <a> so this app's loader never prefetches main-site page-data.
  const resourceOrWikiPathPrefixes = getResourceOrWikiPathPrefixes()
  let inResourceOrWikiApp = false
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname || ''
    inResourceOrWikiApp = resourceOrWikiPathPrefixes.some(prefix =>
      currentPath.startsWith(prefix),
    )
  }

  const isCrossAppRoute = isCrossAppTarget(regionPath, resourceOrWikiPathPrefixes)
  const hasHashFragment = regionPath.includes('#')

  // For cross-app routes (resource & wiki sites, including regional variants
  // like /middle-east/resources/...), avoid Gatsby Link entirely so that
  // any Gatsby app's loader never tries to prefetch and load component
  // chunks that belong to a different Gatsby app.
  //
  // - From MAIN app to /resources/*, /reports/*, /wiki/*, etc. → plain <a>
  // - From RESOURCE/WIKI app to anywhere (including main) → plain <a>
  // - Hash links (e.g. /customers/#case-studies) → plain <a> for reliable scroll
  if (isCrossAppRoute || hasHashFragment || inResourceOrWikiApp) {
    const { sx: passThroughSx, ...rest } = otherProps
    return (
      <Box
        as="a"
        href={regionPath}
        {...rest}
        sx={{
          textDecoration: 'none',
          '&:hover': { textDecoration: 'none' },
          ...(passThroughSx || {}),
        }}
      >
        {children}
      </Box>
    )
  }

  return (
    <StyledLink {...otherProps} to={regionPath} activeClassName="active">
      {children}
    </StyledLink>
  )
}

export default RegionalLink
